import * as StoreTypes from "../DataTypes/storeTypes";
import * as Fetch from "../../Common/fetch";
import * as ActionGenerics from "../../Common/actionGenerics";
import * as ActionTypes from "./actionTypes";
import * as LocalStorage from "../../Common/localStorageManager";
import * as StorageData from "../DataTypes/localStorage";
import * as HelperTypes from "../DataTypes/helperTypes";
import * as AccountModel from "../../Common/accountModel";
import * as Notifications from "../../Common/Notifications/helper";
import * as StoreManager from "../../AppointmentBooking/storeManager";
import { parsePhoneNumberFromString, parseDigits, CountryCode } from "libphonenumber-js";

export function onLogoutClicked() {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        const state = getState();

        dispatch(updateIsBusy(true));

        StoreManager.flush(state.initialSettings.organisationInfo.sgId);

        return Fetch.get<boolean>(state.initialSettings.logOutUrl)
            .then((data) => state.initialSettings.onLoggedIn(null))
            .catch((data) => Notifications.error(data.message));
    };
}

export function updateState(
    state: StoreTypes.State
): ActionGenerics.IParamAction<ActionTypes.Actions, StoreTypes.State> {
    return { type: ActionTypes.Actions.UpdateState, parameter: state };
}

export function onLoginSubmit(values: HelperTypes.IUserDetails) {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        const state = getState();

        dispatch(updateIsBusy(true));
        dispatch(setErrorMessage(null));

        return Fetch.post<AccountModel.IAccountModel>(state.initialSettings.loginUrl, values)
            .then((data) => {
                const storageData: StorageData.IFormData = {
                    rememberMe: values.rememberMe,
                    login: values.rememberMe ? values.email : undefined,
                };

                LocalStorage.setData(LocalStorage.DataType.LoginPage, storageData);

                state.initialSettings.onLoggedIn(data);

                if (state.initialSettings.redirectUrl) {
                    window.location.assign(state.initialSettings.redirectUrl);
                }
            })
            .catch((data) => {
                dispatch(updateIsBusy(false));
                dispatch(setErrorMessage(data.message));
            });
    };
}

function updateIsBusy(isBusy: boolean): ActionGenerics.IParamAction<ActionTypes.Actions, boolean> {
    return { type: ActionTypes.Actions.UpdateIsBusy, parameter: isBusy };
}

function setErrorMessage(message: string): ActionGenerics.IParamAction<ActionTypes.Actions, string> {
    return { type: ActionTypes.Actions.SetErrorMessage, parameter: message };
}

function updateRecoverState(): ActionGenerics.IAction<ActionTypes.Actions> {
    return { type: ActionTypes.Actions.UpdateRecoverState };
}

function resetRecoverState(): ActionGenerics.IAction<ActionTypes.Actions> {
    return { type: ActionTypes.Actions.ResetRecoverState };
}

function updateSetExistingClientCredentialsState(): ActionGenerics.IAction<ActionTypes.Actions> {
    return { type: ActionTypes.Actions.UpdateSetExistingClientCredentialsState };
}

function resetSetExistingClientCredentialsState(): ActionGenerics.IAction<ActionTypes.Actions> {
    return { type: ActionTypes.Actions.ResetSetExistingClientCredentialsState };
}

function resetRegisterState(): ActionGenerics.IAction<ActionTypes.Actions> {
    return { type: ActionTypes.Actions.ResetRegisterState };
}

export function onRecoverSubmit(values, resetInputToken) {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        const state = getState();

        dispatch(updateIsBusy(true));
        dispatch(setErrorMessage(null));

        return Fetch.post<AccountModel.IAccountModel>(state.initialSettings.recoverUrl, values)
            .then((data) => {
                if (state.common.recoverPageState === StoreTypes.TokenState.ShowToken) {
                    state.initialSettings.onLoggedIn(data);
                }

                dispatch(updateIsBusy(false));
                dispatch(updateRecoverState());
            })
            .catch((data) => {
                const errorObj = JSON.parse(data.message);
                dispatch(updateIsBusy(false));
                dispatch(setErrorMessage(errorObj.message));

                if (errorObj.tokenDoesNotExist) {
                    resetInputToken();
                    dispatch(resetRecoverState());
                }
            });
    };
}

export function onSetExistingClientCredentialsSubmit(values, resetInputToken) {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        const state = getState();

        values.clientPublicId = state.common?.clientPublicId;

        dispatch(updateIsBusy(true));
        dispatch(setErrorMessage(null));

        const processMessage = (data) => {
            let message;
            let additionalProperties: any = {};

            // We can receive different object structures from the endpoint due to the shadow redirect to Log in POST
            // Therefore we need to make sure we always show a descriptive message to the user
            try {
                let parsedMessage = JSON.parse(data.message);

                message = parsedMessage.message;
                additionalProperties = { ...parsedMessage };
                delete additionalProperties.message;
            } catch (e) {
                // If parsing fails, treat data.message as a plain string
                message = data.message;
            }

            return {
                message: message,
                ...additionalProperties,
            };
        };

        return Fetch.post<AccountModel.IAccountModel>(state.initialSettings.setExistingClientCredentialsUrl, values)
            .then((data) => {
                if (state.common.setExistingClientCredentialsPageState === StoreTypes.TokenState.ShowToken) {
                    state.initialSettings.onLoggedIn(data);
                }

                dispatch(updateIsBusy(false));
                dispatch(updateSetExistingClientCredentialsState());
            })
            .catch((data) => {
                const errorObj = processMessage(data);
                dispatch(updateIsBusy(false));
                dispatch(setErrorMessage(errorObj.message));

                if (errorObj.tokenDoesNotExist) {
                    resetInputToken();
                    dispatch(resetSetExistingClientCredentialsState());
                }
            });
    };
}

function updateRegisterState(): ActionGenerics.IAction<ActionTypes.Actions> {
    return { type: ActionTypes.Actions.UpdateRegisterState };
}

export function onRegisterSubmit(values, resetInputToken) {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        const state = getState();
        values.supplierId = state.initialSettings.supplierId;

        dispatch(updateIsBusy(true));
        dispatch(setErrorMessage(null));

        const phoneNumber = values.phoneNumber;

        if (phoneNumber) {
            const countryCode = state.initialSettings.organisationInfo.countryCode;
            const formatted = formatPhoneNumber(phoneNumber, countryCode);
            values = { ...values };

            values.phoneNumber = parseDigits(formatted);
        }

        return Fetch.post<AccountModel.IAccountModel>(state.initialSettings.registerUrl, values)
            .then((data) => {
                if (state.common.registerPageState === StoreTypes.TokenState.ShowToken) {
                    state.initialSettings.onLoggedIn(data);
                }

                dispatch(updateIsBusy(false));
                dispatch(updateRegisterState());
            })
            .catch((data) => {
                const errorObj = JSON.parse(data.message);
                dispatch(updateIsBusy(false));
                dispatch(setErrorMessage(errorObj.message));

                if (errorObj.tokenDoesNotExist) {
                    resetInputToken();
                    dispatch(resetRegisterState());
                }
            });
    };
}

function formatPhoneNumber(phoneNumber: string, countryCode: CountryCode): string {
    if (countryCode === "AU") {
        const cleanPhoneNumber = phoneNumber.trim().replace(/\s/g, "");
        if (
            (cleanPhoneNumber.length === 10 && cleanPhoneNumber.startsWith("05")) ||
            (cleanPhoneNumber.length === 12 && cleanPhoneNumber.startsWith("+615"))
        ) {
            return formatUnusualAuNumberAsNational(cleanPhoneNumber);
        }
    }

    return parsePhoneNumberFromString(phoneNumber, countryCode).formatNational();
}

function formatUnusualAuNumberAsNational(number: string): string {
    if (number.startsWith("+615")) {
        return number.replace("+615", "05");
    }

    return number;
}

export function updateClientData(
    data: AccountModel.IAccountModel
): ActionGenerics.IParamAction<ActionTypes.Actions, AccountModel.IAccountModel> {
    return { type: ActionTypes.Actions.UpdateClientData, parameter: data };
}

export function updateClientPublicId(
    data: AccountModel.IAccountModel
): ActionGenerics.IParamAction<ActionTypes.Actions, AccountModel.IAccountModel> {
    return { type: ActionTypes.Actions.UpdateClientPublicId, parameter: data };
}
